import { useMutation, type UseMutationOptions, type UseMutationResult } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { UserProfile, Pool, UpdatePoolBody } from "./generated/api.schemas";

export const loginAdminSession = async (gatewayPasswordHash: string): Promise<void> => {
  await customFetch("/api/auth/admin-session", {
    method: "POST",
    body: JSON.stringify({ gatewayPasswordHash }),
    credentials: "include",
  });
};

export const verifyAdminSession = async (): Promise<{ valid: boolean }> => {
  return customFetch<{ valid: boolean }>("/api/auth/admin-session/verify", {
    credentials: "include",
  });
};

export const logoutAdminSession = async (): Promise<void> => {
  await customFetch("/api/auth/admin-session", {
    method: "DELETE",
    credentials: "include",
  });
};

export type VersionResponse = { version: string | null };

export const getVersion = async (): Promise<VersionResponse> => {
  return customFetch<VersionResponse>("/api/version");
};

export type AdminUpdateDebtBody = { status: "queued" | "rejected"; reason?: string };
export type AdminUpdateDebtResult = { id: number; status: string };

export const adminUpdateDebt = async (
  id: number,
  body: AdminUpdateDebtBody,
  options?: RequestInit,
): Promise<AdminUpdateDebtResult> => {
  return customFetch<AdminUpdateDebtResult>(`/api/admin/debts/${id}`, {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(body),
  });
};

export type SetMyRoleInput = { role: "donor" | "debtor"; deviceFingerprintHash?: string };

export const setMyRole = async (input: SetMyRoleInput): Promise<UserProfile> => {
  return customFetch<UserProfile>("/api/users/me/role", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
};

export const useSetMyRole = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<UserProfile, TError, SetMyRoleInput, TContext>;
}): UseMutationResult<UserProfile, TError, SetMyRoleInput, TContext> => {
  return useMutation<UserProfile, TError, SetMyRoleInput, TContext>({
    mutationFn: (input) => setMyRole(input),
    ...options?.mutation,
  });
};

export const useAdminUpdateDebt = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    AdminUpdateDebtResult,
    TError,
    { id: number; data: AdminUpdateDebtBody },
    TContext
  >;
}): UseMutationResult<
  AdminUpdateDebtResult,
  TError,
  { id: number; data: AdminUpdateDebtBody },
  TContext
> => {
  return useMutation<AdminUpdateDebtResult, TError, { id: number; data: AdminUpdateDebtBody }, TContext>({
    mutationFn: ({ id, data }) => adminUpdateDebt(id, data),
    ...options?.mutation,
  });
};

export const updatePool = async (
  id: number,
  body: UpdatePoolBody,
  options?: RequestInit,
): Promise<Pool> => {
  return customFetch<Pool>(`/api/pools/${id}`, {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(body),
  });
};

export const useUpdatePool = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Pool, TError, { id: number; data: UpdatePoolBody }, TContext>;
}): UseMutationResult<Pool, TError, { id: number; data: UpdatePoolBody }, TContext> => {
  return useMutation<Pool, TError, { id: number; data: UpdatePoolBody }, TContext>({
    mutationFn: ({ id, data }) => updatePool(id, data),
    ...options?.mutation,
  });
};

// ─── Payout Batches ───────────────────────────────────────────────────────────

export interface PayoutReadyDebt {
  id: number;
  creditor: string;
  creditorAddress: string | null;
  accountNumber: string | null;
  accountHolderName: string | null;
  balance: number;
  effectiveAmount: number;
  status: string;
  verificationStatus: string;
  queuePosition: number | null;
  creditorBankingStatus: {
    verificationStatus: "pending" | "verified" | "failed";
    inviteSentAt: string | null;
    reminderSentAt: string | null;
  } | null;
}

export interface PayoutReadyPool {
  id: number;
  name: string;
  balance: number;
  threshold: number;
  status: string;
}

export interface PayoutReadyResponse {
  pool: PayoutReadyPool;
  debts: PayoutReadyDebt[];
}

export const getPayoutReady = async (poolId: number): Promise<PayoutReadyResponse> =>
  customFetch<PayoutReadyResponse>(`/api/admin/pools/${poolId}/payout-ready`);

export interface CreatePayoutBatchBody {
  poolId: number;
  debtIds: number[];
  paymentMethod: "wire" | "ach" | "check" | "other";
  reference?: string;
  notes?: string;
  paymentDate?: string;
}

export interface PayoutBatch {
  id: number;
  poolId: number;
  poolName?: string;
  debtIds: number[];
  debtsCount: number;
  totalAmount: number;
  paymentMethod: string;
  reference: string | null;
  notes: string | null;
  status: string;
  executedBy: string;
  executedAt: string;
  approvedByClerkId: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export interface PayoutBatchDetail extends PayoutBatch {
  pool: { id: number; name: string } | null;
  debts: {
    id: number;
    creditor: string;
    creditorAddress: string | null;
    accountNumber: string | null;
    accountHolderName: string | null;
    balance: number;
    effectiveAmount: number;
    status: string;
    payoffConfirmationUrl: string | null;
    payoffConfirmedAt: string | null;
  }[];
}

export const createPayoutBatch = async (body: CreatePayoutBatchBody): Promise<PayoutBatch> =>
  customFetch<PayoutBatch>("/api/admin/payout-batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const listPayoutBatches = async (): Promise<PayoutBatch[]> =>
  customFetch<PayoutBatch[]>("/api/admin/payout-batches");

export const getPayoutBatch = async (id: number): Promise<PayoutBatchDetail> =>
  customFetch<PayoutBatchDetail>(`/api/admin/payout-batches/${id}`);

export const confirmPayoutBatch = async (
  id: number,
): Promise<PayoutBatch & { debtsMarkedPaid: number; donorAcknowledgmentsSent: number; skipped1099cDebtIds: number[] }> =>
  customFetch<PayoutBatch & { debtsMarkedPaid: number; donorAcknowledgmentsSent: number; skipped1099cDebtIds: number[] }>(
    `/api/admin/payout-batches/${id}/confirm`,
    { method: "PATCH" },
  );

export const approvePayoutBatch = async (id: number): Promise<{ id: number; status: string; approvedByClerkId: string; approvedAt: string }> =>
  customFetch<{ id: number; status: string; approvedByClerkId: string; approvedAt: string }>(
    `/api/admin/payout-batches/${id}/approve`,
    { method: "POST" },
  );

export const rejectPayoutBatch = async (id: number, reason?: string): Promise<{ id: number; status: string; approvedByClerkId: string; rejectionReason: string | null }> =>
  customFetch<{ id: number; status: string; approvedByClerkId: string; rejectionReason: string | null }>(
    `/api/admin/payout-batches/${id}/reject`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    },
  );

export interface CreditorTransfer {
  id: number;
  payoutBatchId: number;
  debtId: number;
  stripeTransferId: string | null;
  amount: number;
  status: "pending" | "in_transit" | "paid" | "failed";
  failureCode: string | null;
  failureMessage: string | null;
  createdAt: string;
  updatedAt: string;
  creditor: string | null;
  accountNumber: string | null;
}

export const getPayoutBatchTransfers = async (batchId: number): Promise<CreditorTransfer[]> =>
  customFetch<CreditorTransfer[]>(`/api/admin/payout-batches/${batchId}/transfers`);

// ─── Job Queue ────────────────────────────────────────────────────────────────

export interface JobSummary {
  completed24h: number;
  failed24h: number;
  total24h: number;
  successRate: number | null;
  avgProcessingSeconds: number | null;
}

export interface JobByType {
  pending: number;
  running: number;
}

export interface ActiveJob {
  id: number;
  type: string;
  status: string;
  attempts: number;
  startedAt: string | null;
  createdAt: string;
}

export interface DeadJob {
  id: number;
  type: string;
  status: string;
  error: string | null;
  attempts: number;
  maxAttempts: number;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface JobQueueResponse {
  summary: JobSummary;
  byType: Record<string, JobByType>;
  activeJobs: ActiveJob[];
  deadJobs: DeadJob[];
}

export interface PendingJobRow {
  id: number;
  type: string;
  priority: number;
  attempts: number;
  runAfter: string;
  createdAt: string;
}

export interface PendingJobsResponse {
  rows: PendingJobRow[];
  total: number;
  page: number;
  pages: number;
}

export const getJobQueue = async (): Promise<JobQueueResponse> =>
  customFetch<JobQueueResponse>("/api/admin/jobs");

export const getPendingJobs = async (page = 1): Promise<PendingJobsResponse> =>
  customFetch<PendingJobsResponse>(`/api/admin/jobs/pending?page=${page}`);

export const retryJob = async (id: number): Promise<{ id: number; status: string; message: string }> =>
  customFetch<{ id: number; status: string; message: string }>(`/api/admin/jobs/${id}/retry`, {
    method: "POST",
  });

export const dismissJob = async (id: number): Promise<{ id: number; dismissed: boolean }> =>
  customFetch<{ id: number; dismissed: boolean }>(`/api/admin/jobs/${id}`, {
    method: "DELETE",
  });

export const purgeDeadJobs = async (): Promise<{ purged: number }> =>
  customFetch<{ purged: number }>("/api/admin/jobs/dead", {
    method: "DELETE",
  });

export const retryAllDeadJobs = async (): Promise<{ requeued: number }> =>
  customFetch<{ requeued: number }>("/api/admin/jobs/retry-all", {
    method: "POST",
  });

export interface SeedPoolsResult {
  inserted: number;
  countBefore: number;
  countAfter: number;
  message: string;
}

export const adminSeedPools = async (): Promise<SeedPoolsResult> =>
  customFetch<SeedPoolsResult>("/api/admin/seed-pools", {
    method: "POST",
    credentials: "include",
  });

// ─── Inbound Inbox ─────────────────────────────────────────────────────────────

export interface InboxMessage {
  id: number;
  fromEmail: string;
  toEmail: string;
  subject: string;
  textBody: string | null;
  htmlBody: string | null;
  isRead: boolean;
  receivedAt: string;
  matchedUserId: number | null;
  matchedUserEmail: string | null;
  matchedUserName: string | null;
}

export interface InboxResponse {
  messages: InboxMessage[];
  total: number;
  unreadCount: number;
  page: number;
  pages: number;
}

export const getAdminInbox = async (page = 1, unreadOnly = false): Promise<InboxResponse> =>
  customFetch<InboxResponse>(`/api/admin/inbox?page=${page}&unread=${unreadOnly}`);

export const markInboxMessageRead = async (id: number): Promise<{ id: number; isRead: boolean }> =>
  customFetch<{ id: number; isRead: boolean }>(`/api/admin/inbox/${id}/read`, {
    method: "PATCH",
  });

export const markAllInboxRead = async (): Promise<{ marked: number }> =>
  customFetch<{ marked: number }>("/api/admin/inbox/read-all", { method: "POST" });

export const replyToInboxMessage = async (
  id: number,
  body: string,
): Promise<{ sent: boolean; to: string; subject: string }> =>
  customFetch<{ sent: boolean; to: string; subject: string }>(`/api/admin/inbox/${id}/reply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });

// ─── Board Officer Session ─────────────────────────────────────────────────────

export const loginBoardSession = async (gatewayKey: string): Promise<{ boardRole: string }> =>
  customFetch<{ boardRole: string }>("/api/auth/board-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ gatewayKey }),
    credentials: "include",
  });

export const verifyBoardSession = async (): Promise<{ valid: boolean; boardRole?: string; userId?: number }> =>
  customFetch<{ valid: boolean; boardRole?: string; userId?: number }>("/api/auth/board-session/verify", {
    credentials: "include",
  });

export const logoutBoardSession = async (): Promise<void> => {
  await customFetch("/api/auth/board-session", {
    method: "DELETE",
    credentials: "include",
  });
};

// ─── Board Officers Admin ─────────────────────────────────────────────────────

export interface BoardOfficer {
  id: number;
  email: string;
  displayName: string | null;
  role: string;
  boardRole: string | null;
  hasKey: boolean;
  createdAt: string;
}

export const getAdminBoardOfficers = async (): Promise<{ officers: BoardOfficer[] }> =>
  customFetch<{ officers: BoardOfficer[] }>("/api/admin/board-officers", {
    credentials: "include",
  });

export const searchAdminUsers = async (q: string): Promise<{ users: AdminUserRow[] }> =>
  customFetch<{ users: AdminUserRow[] }>(`/api/admin/users/search?q=${encodeURIComponent(q)}`, {
    credentials: "include",
  });

export const activateBoardRole = async (userId: number, role: "secretary" | "treasurer"): Promise<{ ok: boolean; plainKey: string; role: string; userId: number; email: string }> =>
  customFetch<{ ok: boolean; plainKey: string; role: string; userId: number; email: string }>(`/api/admin/users/${userId}/activate-board-role`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
    credentials: "include",
  });

export const deactivateBoardRole = async (userId: number): Promise<{ ok: boolean; userId: number }> =>
  customFetch<{ ok: boolean; userId: number }>(`/api/admin/users/${userId}/board-role`, {
    method: "DELETE",
    credentials: "include",
  });

// ─── Secretary Dashboard Data ────────────────────────────────────────────────

export interface SecretaryInboxMessage {
  id: number;
  fromEmail: string;
  toEmail: string;
  subject: string;
  textBody: string | null;
  isRead: boolean;
  receivedAt: string;
}

export interface SecretaryAuditLog {
  id: number;
  action: string;
  actorEmail: string | null;
  entityType: string | null;
  entityId: number | null;
  createdAt: string;
}

export interface SecretaryMember {
  id: number;
  email: string;
  displayName: string | null;
  role: string;
  createdAt: string;
}

export interface SecretaryData {
  inbox: { messages: SecretaryInboxMessage[]; total: number; page: number; pages: number };
  auditLogs: SecretaryAuditLog[];
  debtStats: { total: number; pending: number; queued: number; processing: number; paid: number; rejected: number };
  members: SecretaryMember[];
  asOf: string;
}

export const getSecretaryData = async (page = 1): Promise<SecretaryData> =>
  customFetch<SecretaryData>(`/api/admin/secretary/data?page=${page}`, {
    credentials: "include",
  });

// ─── Treasurer Dashboard Data ─────────────────────────────────────────────────

export interface TreasurerPool {
  id: number;
  name: string;
  balance: number;
  threshold: number;
  status: string;
  category: string | null;
  percentFunded: number;
}

export interface TreasurerBatch {
  id: number;
  totalAmount: number;
  debtsCount: number;
  status: string;
  paymentMethod: string;
  createdAt: string;
}

export interface TreasurerCreditorBanking {
  byStatus: Record<string, number>;
  total: number;
  pendingVerification: number;
  verified: number;
  failed: number;
}

export interface TreasurerData {
  financials: { totalDonations: number; donationCount: number; totalPaidOut: number; paidBatchCount: number; netReserve: number };
  pools: TreasurerPool[];
  pendingBatches: TreasurerBatch[];
  recentBatches: TreasurerBatch[];
  creditorBanking: TreasurerCreditorBanking;
  asOf: string;
}

export const getTreasurerData = async (): Promise<TreasurerData> =>
  customFetch<TreasurerData>("/api/admin/treasurer/data", {
    credentials: "include",
  });

export const approveTreasurerBatch = async (batchId: number): Promise<{ ok: boolean; newStatus: string }> =>
  customFetch<{ ok: boolean; newStatus: string }>(`/api/admin/treasurer/payout-batches/${batchId}/approve`, {
    method: "POST",
    credentials: "include",
  });

export const blockTreasurerBatch = async (batchId: number): Promise<{ ok: boolean; newStatus: string }> =>
  customFetch<{ ok: boolean; newStatus: string }>(`/api/admin/treasurer/payout-batches/${batchId}/block`, {
    method: "POST",
    credentials: "include",
  });

export interface AdminUserRow {
  id: number;
  email: string;
  displayName: string | null;
  role: string;
  boardRole: string | null;
  hasKey: boolean;
  createdAt: string;
}

export const deleteAdminUser = async (id: number): Promise<{ deleted: boolean; userId: number; email: string }> =>
  customFetch<{ deleted: boolean; userId: number; email: string }>(`/api/admin/users/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

export const getAdminAllUsers = async (page = 1): Promise<{
  users: AdminUserRow[];
  page: number;
  totalPages: number;
  total: number;
}> =>
  customFetch<{ users: AdminUserRow[]; page: number; totalPages: number; total: number }>(
    `/api/admin/users?page=${page}`,
    { credentials: "include" },
  );
